import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Badge } from '../../../components/ui/badge';
import { 
  BarChart3, 
  Activity, 
  Shield, 
  AlertTriangle, 
  FileText, 
  Settings 
} from 'lucide-react';
import { LoggingOverviewPanel } from './logging/LoggingOverviewPanel';
import { UserActivitiesPanel } from './logging/UserActivitiesPanel';
import { SecurityEventsPanel } from './logging/SecurityEventsPanel';
import { SecurityAlertsPanel } from './logging/SecurityAlertsPanel';
import { ReportsPanel } from './logging/ReportsPanel';
import { LoggingSettingsPanel } from './logging/LoggingSettingsPanel';
import { useLoggingWebSocket } from '../../../hooks/useLoggingWebSocket';
import { loggingApiService } from '../../../services/loggingApiService';

type LoggingSubTab = 'overview' | 'activities' | 'security' | 'alerts' | 'reports' | 'settings';

interface RealTimeData {
  recentActivityCount: number;
  securityEventCount: number;
  activeAlertCount: number;
  latestSecurityEvent?: any;
  latestAlert?: any;
}

/**
 * User Logging Tab Component
 * 
 * Provides comprehensive user activity monitoring, security event tracking,
 * and compliance reporting for law firm administrators.
 */
export const UserLoggingTab: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<LoggingSubTab>('overview');
  const [realTimeData, setRealTimeData] = useState<RealTimeData>({
    recentActivityCount: 0,
    securityEventCount: 0,
    activeAlertCount: 0,
  });
  const [systemHealth, setSystemHealth] = useState<'healthy' | 'unhealthy' | 'loading'>('loading');

  // Initialize WebSocket for real-time updates
  const { isConnected, lastActivity, lastSecurityEvent, lastAlert } = useLoggingWebSocket();

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [health, activeAlerts, recentActivities] = await Promise.all([
          loggingApiService.getSystemHealth(),
          loggingApiService.getActiveAlerts(),
          loggingApiService.getRecentActivities(1) // Last hour
        ]);

        setSystemHealth(health.status);
        setRealTimeData(prev => ({
          ...prev,
          activeAlertCount: activeAlerts.length,
          recentActivityCount: recentActivities.length
        }));
      } catch (error) {
        console.error('Failed to load initial logging data:', error);
        setSystemHealth('unhealthy');
      }
    };

    loadInitialData();
  }, []);

  // Update real-time data from WebSocket events
  useEffect(() => {
    if (lastActivity) {
      setRealTimeData(prev => ({
        ...prev,
        recentActivityCount: prev.recentActivityCount + 1
      }));
    }
  }, [lastActivity]);

  useEffect(() => {
    if (lastSecurityEvent) {
      setRealTimeData(prev => ({
        ...prev,
        securityEventCount: prev.securityEventCount + 1,
        latestSecurityEvent: lastSecurityEvent
      }));
    }
  }, [lastSecurityEvent]);

  useEffect(() => {
    if (lastAlert) {
      setRealTimeData(prev => ({
        ...prev,
        activeAlertCount: prev.activeAlertCount + 1,
        latestAlert: lastAlert
      }));

      // Show urgent notification for critical alerts
      if (lastAlert.priority === 'critical') {
        // TODO: Implement toast notification
        console.log('🚨 CRITICAL ALERT:', lastAlert.title);
      }
    }
  }, [lastAlert]);

  const subTabs = [
    {
      id: 'overview' as LoggingSubTab,
      label: 'Overview',
      icon: BarChart3,
      description: 'System metrics and real-time activity dashboard',
      showBadge: false
    },
    {
      id: 'activities' as LoggingSubTab,
      label: 'User Activities',
      icon: Activity,
      description: 'Live user activity monitoring and filtering',
      count: realTimeData.recentActivityCount,
      showBadge: realTimeData.recentActivityCount > 0
    },
    {
      id: 'security' as LoggingSubTab,
      label: 'Security Events',
      icon: Shield,
      description: 'Security incident tracking and investigation',
      count: realTimeData.securityEventCount,
      showBadge: realTimeData.securityEventCount > 0
    },
    {
      id: 'alerts' as LoggingSubTab,
      label: 'Security Alerts',
      icon: AlertTriangle,
      description: 'Active security alerts requiring attention',
      count: realTimeData.activeAlertCount,
      showBadge: realTimeData.activeAlertCount > 0,
      badgeVariant: (realTimeData.activeAlertCount > 0 ? 'destructive' : 'secondary') as 'destructive' | 'secondary'
    },
    {
      id: 'reports' as LoggingSubTab,
      label: 'Reports',
      icon: FileText,
      description: 'Compliance and audit report generation',
      showBadge: false
    },
    {
      id: 'settings' as LoggingSubTab,
      label: 'Settings',
      icon: Settings,
      description: 'Logging configuration and preferences',
      showBadge: false
    }
  ];

  return (
    <div className="space-y-6">
      {/* System Status Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            User Logging System
          </h2>
          
          {/* System Health Indicator */}
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${
              systemHealth === 'healthy' ? 'bg-green-400 animate-pulse' :
              systemHealth === 'unhealthy' ? 'bg-red-400' :
              'bg-yellow-400 animate-pulse'
            }`} />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              System {systemHealth === 'loading' ? 'Loading...' : systemHealth}
            </span>
          </div>

          {/* WebSocket Connection Status */}
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              isConnected ? 'bg-green-400 animate-pulse' : 'bg-gray-400'
            }`} />
            <span className="text-xs text-gray-500">
              {isConnected ? 'Live Updates' : 'Disconnected'}
            </span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex items-center space-x-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-lex-accent">
              {realTimeData.recentActivityCount}
            </div>
            <div className="text-xs text-gray-500">Recent Activities</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {realTimeData.securityEventCount}
            </div>
            <div className="text-xs text-gray-500">Security Events</div>
          </div>
          
          <div className="text-center">
            <div className={`text-2xl font-bold ${
              realTimeData.activeAlertCount > 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              {realTimeData.activeAlertCount}
            </div>
            <div className="text-xs text-gray-500">Active Alerts</div>
          </div>
        </div>
      </div>

      {/* Sub-Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <Tabs value={activeSubTab} onValueChange={(value) => setActiveSubTab(value as LoggingSubTab)}>
          <div className="border-b border-gray-200 dark:border-gray-700 px-6">
            <TabsList className="grid w-full grid-cols-6 bg-gray-100 dark:bg-gray-700">
              {subTabs.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 relative group"
                >
                  <div className="flex items-center space-x-2">
                    <tab.icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    
                    {/* Real-time count badges */}
                    {tab.showBadge && tab.count !== undefined && tab.count > 0 && (
                      <Badge 
                        variant={tab.badgeVariant || 'secondary'}
                        className="ml-1 px-1.5 py-0.5 text-xs min-w-[20px] h-5"
                      >
                        {tab.count > 99 ? '99+' : tab.count}
                      </Badge>
                    )}
                  </div>

                  {/* Tooltip with description */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    {tab.description}
                  </div>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            <TabsContent value="overview" className="mt-0">
              <LoggingOverviewPanel />
            </TabsContent>
            
            <TabsContent value="activities" className="mt-0">
              <UserActivitiesPanel />
            </TabsContent>
            
            <TabsContent value="security" className="mt-0">
              <SecurityEventsPanel />
            </TabsContent>
            
            <TabsContent value="alerts" className="mt-0">
              <SecurityAlertsPanel />
            </TabsContent>
            
            <TabsContent value="reports" className="mt-0">
              <ReportsPanel />
            </TabsContent>
            
            <TabsContent value="settings" className="mt-0">
              <LoggingSettingsPanel />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}; 